import React, { useState, useEffect } from 'react';
import PatientDashboardLayout from './PatientDashboardLayout';
import localForage from 'localforage';
import { useBaseUrl } from '../utils/utils';

interface Transaction {
    id: string;
    reference: string;
    amount: number;
    currency: string;
    type: 'credit' | 'debit';
    status: 'pending' | 'completed' | 'failed' | 'cancelled';
    description: string;
    timestamp: string;
    gate?: string;
    fees?: number;
    balance_after?: number;
}

interface BillingAccount {
    balance: number;
    currency: string;
    account_number: string;
    status: 'active' | 'suspended' | 'pending';
}

interface Invoice {
    inv_code: string;
    inv_name: string;
    inv_date: string;
    total_amount: number;
    paid_amount: number;
    balance: number;
}

const PatientBilling: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [account, setAccount] = useState<BillingAccount | null>(null);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [invoicesError, setInvoicesError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [currentTab, setCurrentTab] = useState('overview');
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('mpesa');
    const [patientEmail, setPatientEmail] = useState<string>('');
    const [patientId, setPatientId] = useState<string>('');

    useEffect(() => {
        initializePatientData();
    }, []);

    const initializePatientData = async () => {
        try {
            const email = await localForage.getItem<string>('patient_email');
            const id = await localForage.getItem<string>('patient_id');
            
            if (email) setPatientEmail(email);
            if (id) setPatientId(id);
            
            if (email || id) {
                fetchBillingData(email || '', id || '');
                fetchInvoices(id || '');
            }
        } catch (error) {
            console.error('Error loading patient data:', error);
        }
    };

    const fetchInvoices = async (id: string) => {
        if (!id) return;
        try {
            const res = await fetch(`${baseUrl}billing/api/billing/invoices/by-patient/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ patient_code: id }),
            });
            const data = await res.json();
            if (data.status === 'success') {
                setInvoices(data.data || []);
                setInvoicesError(false);
            } else {
                setInvoicesError(true);
            }
        } catch (error) {
            console.error('Failed to fetch invoices:', error);
            setInvoicesError(true);
        }
    };

    const fetchBillingData = async (email: string, id: string) => {
        setLoading(true);
        try {
            // Fetch account balance
            const balanceResponse = await fetch(`${baseUrl}billing/api/balance/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: email,
                    patient_id: id
                })
            });

            const balanceData = await balanceResponse.json();
            if (balanceData.status === 'success') {
                setAccount(balanceData.data.account);
            }

            // Fetch recent transactions
            const transactionsResponse = await fetch(`${baseUrl}billing/api/transactions/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: email,
                    patient_id: id
                })
            });

            const transactionsData = await transactionsResponse.json();
            if (transactionsData.status === 'success') {
                setTransactions(transactionsData.data.transactions || []);
            }
        } catch (error) {
            console.error('Failed to fetch billing data:', error);
            setAccount(null);
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number, currency: string = 'KES') => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-KE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'failed': return 'bg-red-100 text-red-800';
            case 'cancelled': return 'bg-gray-100 text-gray-800';
            default: return 'bg-blue-100 text-blue-800';
        }
    };

    const handlePayment = async () => {
        if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
            alert('Please enter a valid amount');
            return;
        }

        if (!patientEmail && !patientId) {
            alert('Patient information not found. Please log in again.');
            return;
        }

        try {
            const response = await fetch(`${baseUrl}billing/api/process-payment/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_email: patientEmail,
                    patient_id: patientId,
                    amount: parseFloat(paymentAmount),
                    payment_method: paymentMethod,
                    description: 'Patient payment'
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                alert('Payment processed successfully!');
                setPaymentAmount('');
                fetchBillingData(patientEmail, patientId); // Refresh data
            } else {
                alert('Payment failed: ' + data.message);
            }
        } catch (error) {
            console.error('Payment error:', error);
            alert('Payment failed. Please try again.');
        }
    };


    if (loading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-lg text-gray-600">Loading billing information...</span>
                </div>
            </div>
        );
    }

    return (
        <PatientDashboardLayout>
            <div className="p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src={`${process.env.PUBLIC_URL}/assets/images/app-billing.png`} alt="Billing" className="w-10 h-10" />
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Billing & Payments</h2>
                            <p className="text-gray-600">Manage your healthcare payments and transactions</p>
                        </div>
                    </div>
                </div>

                {/* Account Overview */}
                {account && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                            <div className="flex items-center justify-between mb-4">
                                <div className="p-3 bg-blue-50 rounded-xl">
                                    <i className="ri-wallet-3-line text-blue-600 text-xl"></i>
                                </div>
                                <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                    {account.status}
                                </span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Account Balance</p>
                                <p className="text-3xl font-bold text-gray-900">{formatCurrency(account.balance, account.currency)}</p>
                                <p className="text-xs text-gray-500 mt-1">Account: {account.account_number}</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                            <div className="flex items-center justify-between mb-4">
                                <div className="p-3 bg-green-50 rounded-xl">
                                    <i className="ri-arrow-up-line text-green-600 text-xl"></i>
                                </div>
                                <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                    This Month
                                </span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Total Credits</p>
                                <p className="text-3xl font-bold text-green-600">
                                    {formatCurrency(transactions.filter(t => t.type === 'credit' && t.status === 'completed').reduce((sum, t) => sum + t.amount, 0))}
                                </p>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                            <div className="flex items-center justify-between mb-4">
                                <div className="p-3 bg-red-50 rounded-xl">
                                    <i className="ri-arrow-down-line text-red-600 text-xl"></i>
                                </div>
                                <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full">
                                    This Month
                                </span>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600 mb-1">Total Debits</p>
                                <p className="text-3xl font-bold text-red-600">
                                    {formatCurrency(transactions.filter(t => t.type === 'debit' && t.status === 'completed').reduce((sum, t) => sum + t.amount, 0))}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-lg border border-gray-100">
                    <div className="border-b border-gray-200">
                        <nav className="flex space-x-8 px-6">
                            <button
                                onClick={() => setCurrentTab('overview')}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                    currentTab === 'overview'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Overview
                            </button>
                            <button
                                onClick={() => setCurrentTab('transactions')}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                    currentTab === 'transactions'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Transactions
                            </button>
                            <button
                                onClick={() => setCurrentTab('invoices')}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                    currentTab === 'invoices'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Invoices
                            </button>
                            <button
                                onClick={() => setCurrentTab('payment')}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                    currentTab === 'payment'
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Make Payment
                            </button>
                        </nav>
                    </div>

                    <div className="p-6">
                        {currentTab === 'overview' && (
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                                    <div className="space-y-3">
                                        {transactions.slice(0, 5).map((transaction) => (
                                            <div key={transaction.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                                <div className="flex items-center">
                                                    <div className={`p-2 rounded-lg mr-4 ${
                                                        transaction.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                                                    }`}>
                                                        <i className={`ri-${transaction.type === 'credit' ? 'arrow-up' : 'arrow-down'}-line ${
                                                            transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                                                        }`}></i>
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900">{transaction.description}</p>
                                                        <p className="text-sm text-gray-500">{transaction.reference}</p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <p className={`font-semibold ${
                                                        transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                        {transaction.type === 'credit' ? '+ (Refund) ' : '-'}{formatCurrency(transaction.amount)}
                                                    </p>
                                                    <p className="text-sm text-gray-500">{formatDate(transaction.timestamp)}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentTab === 'transactions' && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-lg font-semibold text-gray-900">All Transactions</h3>
                                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                                        Export
                                    </button>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {transactions.map((transaction) => (
                                                <tr key={transaction.id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {transaction.reference}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        <span className={transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}>
                                                            {transaction.type === 'credit' ? '+ (Refund) ' : '-'}{formatCurrency(transaction.amount)}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                            transaction.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                        }`}>
                                                            {transaction.type === 'credit' ? 'Refund' : 'Debit'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                                                            {transaction.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {formatDate(transaction.timestamp)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {currentTab === 'invoices' && (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-lg font-semibold text-gray-900">Invoices</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {invoices.map((invoice) => (
                                                <tr key={invoice.inv_code} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{invoice.inv_code}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invoice.inv_name}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(invoice.total_amount)}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(invoice.inv_date)}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${invoice.balance <= 0 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{invoice.balance <= 0 ? 'Paid' : 'Unpaid'}</span>
                                                    </td>
                                                </tr>
                                            ))}
                                            {invoices.length === 0 && (
                                                <tr>
                                                    <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                                                        {invoicesError ? 'Could not load invoices. Please try again later.' : 'No invoices on file yet.'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {currentTab === 'payment' && (
                            <div className="max-w-md mx-auto">
                                <h3 className="text-lg font-semibold text-gray-900 mb-6">Make a Payment</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Amount (KES)</label>
                                        <input
                                            type="number"
                                            value={paymentAmount}
                                            onChange={(e) => setPaymentAmount(e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter amount"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                                        <select
                                            value={paymentMethod}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="mpesa">M-Pesa</option>
                                            <option value="airtel">Airtel Money</option>
                                            <option value="card">Credit/Debit Card</option>
                                            <option value="bank">Bank Transfer</option>
                                        </select>
                                    </div>
                                    <button
                                        onClick={handlePayment}
                                        className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                                    >
                                        Process Payment
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PatientDashboardLayout>
    );
};

export default PatientBilling; 